import {
  type ChangeEventHandler,
  type KeyboardEventHandler,
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';

import { type Form } from '@air/api/types';
import { Input } from '@air/primitive-input';
import { Label } from '@air/primitive-label';
import { VisuallyHidden } from '@air/primitive-visually-hidden';

import { CONTENT_COLLECTION_FORM_MODAL_EDITABLE_TITLE } from '~/constants/testIDs';
import { centralizedBoardTitleSelector } from '~/store/centralizedBoard/selectors';
import { useUpdateForm } from '~/swr-hooks/forms/useUpdateForm';

type FormTitleProps = {
  form: Form;
};

export const FormTitle = memo(({ form }: FormTitleProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const boardTitle = useSelector(centralizedBoardTitleSelector);
  const { updateForm } = useUpdateForm();

  const defaultValue = form?.title || boardTitle || '';
  const [value, setValue] = useState(defaultValue);
  const [isValid, setIsValid] = useState(!!value);

  useEffect(() => {
    setValue(defaultValue);
  }, [defaultValue]);

  const onSubmit = useCallback(async () => {
    if (!form) {
      setIsValid(false);
      return;
    }

    if (!value) {
      setIsValid(false);
      return;
    }

    await updateForm({
      ...form,
      title: value,
    });

    setIsValid(true);
  }, [form, updateForm, value]);

  const onChange = useCallback<ChangeEventHandler<HTMLInputElement>>((event) => {
    setValue(event.target.value);
  }, []);

  const onKeyDown = useCallback<KeyboardEventHandler<HTMLInputElement>>((event) => {
    if (event.key === 'Enter') {
      inputRef.current?.blur();
    }
  }, []);

  return (
    <div data-testid={CONTENT_COLLECTION_FORM_MODAL_EDITABLE_TITLE}>
      <VisuallyHidden>
        <Label htmlFor="form-title">Form title</Label>
      </VisuallyHidden>
      <Input
        className="w-full"
        hasError={!isValid}
        defaultValue={value}
        id="form-title"
        onChange={onChange}
        onKeyDown={onKeyDown}
        onBlur={onSubmit}
        placeholder="Add your form title"
        ref={inputRef}
        size="extra-large"
        value={value}
        required
      />
      {!isValid && <p className="text-12 text-red-9">Form title is required</p>}
    </div>
  );
});

FormTitle.displayName = 'FormTitle';
